import {Injectable, NgZone} from '@angular/core';
import {Observable, Observer} from 'rxjs';

import {CordovaService} from '../cordova/cordova.service';
import {LayoutMgrService} from "../layout-mgr/layout-mgr.service";
import {INAPPBROWSER_CLOSE_BUTTON_CAPTION} from '../../defines/client.const';
import {environment} from "../../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class YoutubeService {

  private youtube: InAppBrowser = null;

  constructor(
    private zone: NgZone,
    private cordova: CordovaService,
    private lm: LayoutMgrService,
  ) { }

  // Promise 기반의 지연 함수 추가
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // 외부 앱 열기 함수
  private async openExternalApp(url: string): Promise<void> {
    try {
      window.open(url, '_system');
    } catch (error) {
      console.error('외부 앱 열기 실패:', error);
    }
  }

  public Open(youtubeId: string): Observable<any>
  {
    if (!this.cordova.onCordova) {
      // return new Observable<boolean>(observer => observer.error('YoutubeService::Open() cordova does not initialized!'));

      return this.lm.OpenYoutubePopup(youtubeId)
    }

    const url = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&playsinline=1&mute=0&rel=0`;
    console.log('YoutubeService::Open() url = ', url);

    this.zone.runOutsideAngular(() => {

      // iOS와 Android에 적합한 옵션 설정
      const options = [
        'location=no',
        'footer=no',
        'fullscreen=yes',
        'allowInlineMediaPlayback=yes',
        'mediaPlaybackRequiresUserAction=no',
        'shouldPauseOnSuspend=no',
        'orientation=landscape',
        `closebuttoncaption=${INAPPBROWSER_CLOSE_BUTTON_CAPTION}`,
        'hidden=no',
        'hardwareback=yes',
        'hidenavigationbuttons=yes',
        `referrer=https://${environment.androidAppId}`,
      ].join(',');

      console.log('YoutubeService::Open() options = ', options);

      this.youtube = this.cordova.cordova.InAppBrowser.open(
        url,
        '_blank',
        options);

      // set to either landscape
      screen.orientation.unlock();

      this.lm.cordovaYoutubeBg = true;
    });

    return new Observable<any>((observer: Observer<any>) => {

      const callbackLoadStop = (event: InAppBrowserEvent) => {
        console.log('YoutubeService:: InAppBrowser LoadStop!');

        // iOS에서만 자동 재생 스크립트 실행
        // - iOS 17.1+ 부터 음소거 안 된 비디오의 자동 재생 정책이 강화됨
        // - WKWebView의 mediaPlaybackRequiresUserAction=no 설정만으로는 부족
        // - JavaScript로 직접 재생 버튼 클릭 및 video.play() 호출 필요
        // - Android는 URL의 autoplay=1 파라미터만으로 자동 재생 가능
        const platform = this.cordova.cordova.platformId;
        if (platform === 'ios') {
          console.log('iOS 자동 재생 스크립트 실행');
          this.youtube.executeScript({code:
                'console.log("executeScript::");' +
                'var playButton = document.getElementsByClassName("ytp-large-play-button")[0];' +
                'console.log(playButton);' +
                'playButton.click();' +
                'document.querySelector("video").play().catch(error => console.log(error));'
            },
            result => console.log(result));
        } else {
          console.log('Android는 autoplay=1만으로 자동 재생됨');
        }
      };

      const callbackExit = (event: InAppBrowserEvent) => {
        this.zone.run(() => {
          console.log('YoutubeService:: InAppBrowser exit!');
          this.youtube.removeEventListener('exit', callbackExit);
          this.youtube.removeEventListener('loadstop', callbackLoadStop);

          // lock the device orientation
          (screen.orientation as any).lock('portrait').then();

          this.lm.cordovaYoutubeBg = false;

          // observer.next(true);
          observer.complete();
        });
      };

      const callbackLoadStart = async (event: InAppBrowserEvent) => {
        console.log('loadstart event url =>', event.url);

        // iOS에서는 intent:// URL이 생성되지 않기 때문에 추가 확인
        const platform = this.cordova.cordova.platformId;
        console.log('현재 플랫폼:', platform);

        // iOS일 경우 별도 처리 필요 없음 (일반 URL 로드 허용)
        if (platform === 'ios') {
          console.log('iOS에서 YouTube 로드 중');
          return;
        }

        // 'http://intent://'와 'intent://' 모두 처리 (URL 중복 문제 해결)
        const normalizedUrl = event.url.replace('http://intent://', 'intent://');

        if (!normalizedUrl.startsWith('intent://')) {
          return; // intent:// URL이 아니면 처리하지 않음
        }

        // InAppBrowser 내에서 intent:// 링크를 처리할 수 없으므로 우선 뷰를 닫는다
        this.youtube.close();

        // URL에서 v= 파라미터 추출 시도
        console.log('정규화된 URL:', normalizedUrl);

        // YouTube ID 추출
        const youtubeId = this.extractYoutubeId(normalizedUrl);

        // 약간의 지연 후 처리 (UI가 정리될 시간 제공)
        await this.sleep(100);

        try {
          if (youtubeId) {
            console.log('YouTube 앱으로 열기:', youtubeId);
            await this.openExternalApp('vnd.youtube:' + youtubeId);
            return;
          }

          // Intent URL 직접 분석
          const intentData = this.extractIntentData(normalizedUrl);
          console.log('Intent 데이터:', intentData);

          if (intentData.scheme === 'vnd.youtube' && intentData.package === 'com.google.android.youtube') {
            // 직접 YouTube 앱 열기
            console.log('YouTube 앱 직접 열기 시도');
            await this.openExternalApp('vnd.youtube://');
          } else if (intentData.scheme) {
            // 추출된 스킴으로 앱 열기
            console.log('스킴으로 앱 열기:', intentData.scheme);
            await this.openExternalApp(intentData.scheme + '://');
          } else {
            // 마지막 대안으로 YouTube 웹사이트 열기
            console.log('YouTube 웹사이트로 이동');
            await this.openExternalApp('https://www.youtube.com');
          }
        } catch (error) {
          console.error('YouTube 링크 처리 중 오류 발생:', error);
        }
      };

      this.youtube.addEventListener('exit', callbackExit);
      this.youtube.addEventListener('loadstop', callbackLoadStop);
      this.youtube.addEventListener('loadstart', callbackLoadStart);

      return () => {
        if (this.youtube) {
          this.youtube.close();
          this.youtube = null;
        }
      };
    });
  }

  // YouTube intent URL에서 URI 추출하는 도우미 함수
  private extractUriFromIntent(intentUrl: string): string {
    const uriMatch = intentUrl.match(/intent:\/\/([^#]+)/);
    return uriMatch ? uriMatch[1] : '';
  }

  // Intent URL에서 패키지 및 스킴 정보 추출
  private extractIntentData(intentUrl: string): { package?: string, scheme?: string } {
    const result: { package?: string, scheme?: string } = {};

    // #Intent; 이후의 데이터 추출
    const intentParamsMatch = intentUrl.match(/#Intent;(.+?)$/);
    if (intentParamsMatch && intentParamsMatch[1]) {
      const params = intentParamsMatch[1].split(';');

      params.forEach(param => {
        // package= 파라미터 추출
        if (param.startsWith('package=')) {
          result.package = param.substring('package='.length);
        }
        // scheme= 파라미터 추출
        else if (param.startsWith('scheme=')) {
          result.scheme = param.substring('scheme='.length);
        }
      });
    }

    return result;
  }

  // YouTube URL에서 비디오 ID 추출하는 함수
  private extractYoutubeId(url: string): string {
    let youtubeId = '';

    // URL에서 v= 파라미터 추출 시도
    const vParamMatch = url.match(/[?&]v=([^&#]+)/);
    if (vParamMatch && vParamMatch[1]) {
      youtubeId = vParamMatch[1];
      console.log('v= 파라미터에서 추출된 YouTube ID:', youtubeId);
    } else {
      // 다른 형식 시도 (youtube.com/v/ 또는 youtu.be/)
      const altFormatMatch = url.match(/youtube\.com\/v\/([^?&#]+)|youtu\.be\/([^?&#]+)/);
      if (altFormatMatch) {
        youtubeId = altFormatMatch[1] || altFormatMatch[2];
        console.log('대체 형식에서 추출된 YouTube ID:', youtubeId);
      }
    }

    return youtubeId;
  }
}
